import { useRef } from "react";
import { Form, Formik, type FormikProps } from "formik";
import * as Yup from "yup";

import { Currency } from "@vega-api/generated/types/currency";
import { CheckboxField } from "@vega-core/components/Form/Fields/CheckboxField";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";

type IndicateInterestFormValues = {
  investmentAmount: CoreMoney;
  acknowledgment: boolean;
};

type IndicateInterestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: number, currency: CoreCurrency) => void;
  isSubmitting?: boolean;
  initialMoney?: CoreMoney;
  dealCurrency?: Currency;
  maxAmount?: CoreMoney;
};

export const IndicateInterestModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialMoney,
  dealCurrency = Currency.USD,
  maxAmount,
}: IndicateInterestModalProps) => {
  const formRef = useRef<FormikProps<IndicateInterestFormValues>>(null);

  let amountSchema = Yup.number().required(
    m.coInvestments_indicateInterest_validation_amountRequired(),
  );

  if (maxAmount) {
    amountSchema = amountSchema.max(
      maxAmount.amount,
      m.coInvestments_indicateInterest_validation_amountExceedsTarget(),
    );
  }

  const validationSchema = Yup.object({
    investmentAmount: Yup.object({
      amount: amountSchema,
      currency: Yup.string().required(),
    }).required(),
    acknowledgment: Yup.boolean()
      .oneOf(
        [true],
        m.coInvestments_indicateInterest_validation_acknowledgmentRequired(),
      )
      .required(),
  });

  const initialValues: IndicateInterestFormValues = {
    investmentAmount: {
      amount: initialMoney?.amount || 0,
      currency: initialMoney?.currency || dealCurrency,
    },
    acknowledgment: false,
  };

  const handleSubmit = (values: IndicateInterestFormValues) => {
    onSubmit(values.investmentAmount.amount, values.investmentAmount.currency);
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: () => formRef.current?.submitForm(),
        mainActionText: m.btn_submit(),
        mainActionVariant: "primary",
        disabled: isSubmitting,
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isSubmitting,
      }}
      dataTestId="indicate-interest-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_indicateInterest_title()}
    >
      <Formik
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form className="max-w-sm mb-2 flex flex-col gap-4">
          <MoneyField
            allowAbbreviations
            currency={dealCurrency}
            isRequired
            label={m.coInvestments_indicateInterest_amount()}
            name="investmentAmount"
          />

          <CheckboxField
            className="text-start"
            label={m.coInvestments_indicateInterest_acknowledgment()}
            name="acknowledgment"
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
